import React, { useEffect, useState } from "react";
import AppContainer from "../../Components/Accounting/AppContainer";
import DataTable from "../../Components/Accounting/DataTable";
import ajaxAccounting from "../../util/accounting/remote/ajaxAccounting";
import functions from "../../util/accounting/functions";
import toast, { Toaster } from "react-hot-toast";
import { RenderSecure } from "../../util/script/RenderSecure";
import AppModal from "../../Components/Accounting/AppModal";

const FinancialYear = () => {
  const [years, setYears] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ name: "", start_date: "", end_date: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => { fetchYears(); }, []);

  const fetchYears = async () => {
    setLoading(true);
    const res = await ajaxAccounting.listFinancialYears({});
    if (res.status === "OK") setYears(res.details || []);
    setLoading(false);
  };

  const handleCreate = async () => {
    if (!form.name || !form.start_date || !form.end_date) { toast.error("All fields required."); return; }
    setSaving(true);
    const res = await ajaxAccounting.createFinancialYear(form);
    if (res.status === "OK") { toast.success("Financial year created!"); fetchYears(); setShowModal(false); setForm({ name: "", start_date: "", end_date: "" }); }
    else toast.error(res.message);
    setSaving(false);
  };

  const handleClose = async (id) => {
    if (!window.confirm("Close this financial year? This action cannot be undone.")) return;
    const res = await ajaxAccounting.closeFinancialYear({ id });
    if (res.status === "OK") { toast.success("Financial year closed."); fetchYears(); }
    else toast.error(res.message);
  };

  const handleLock = async (id, isLocked) => {
    const fn = isLocked ? ajaxAccounting.unlockFinancialYear : ajaxAccounting.lockFinancialYear;
    const res = await fn({ id });
    if (res.status === "OK") { toast.success(`Financial year ${isLocked ? "unlocked" : "locked"}.`); fetchYears(); }
    else toast.error(res.message);
  };

  const statusBadge = (row) => {
    if (row.is_closed) return <span className="badge bg-secondary">Closed</span>;
    if (row.is_locked) return <span className="badge bg-danger">Locked</span>;
    if (row.is_current) return <span className="badge bg-success">Current</span>;
    return <span className="badge bg-info">Open</span>;
  };

  const columns = [
    { key: "name", label: "Financial Year", render: (v, row) => <strong>{v}{row.is_current ? <span className="badge bg-success ms-2">Current</span> : ""}</strong> },
    { key: "start_date", label: "Start Date", render: (v) => functions.formatDate(v) },
    { key: "end_date", label: "End Date", render: (v) => functions.formatDate(v) },
    { key: "status", label: "Status", render: (_, row) => statusBadge(row) },
    {
      key: "id", label: "Actions",
      render: (id, row) => (
        <div className="d-flex gap-1">
          <RenderSecure code="ACC-FY-LOCK">
            {!row.is_closed && (
              <button className={`btn btn-sm ${row.is_locked ? "btn-outline-success" : "btn-outline-warning"}`} onClick={() => handleLock(id, row.is_locked)}>
                <i className={`fa-solid ${row.is_locked ? "fa-lock-open" : "fa-lock"}`}></i>
              </button>
            )}
          </RenderSecure>
          <RenderSecure code="ACC-FY-CLOSE">
            {!row.is_closed && (
              <button className="btn btn-sm btn-outline-danger" onClick={() => handleClose(id)} title="Close Year">
                <i className="fa-solid fa-circle-xmark"></i>
              </button>
            )}
          </RenderSecure>
        </div>
      ),
    },
  ];

  // Auto-generate year name suggestion
  const suggestName = () => {
    if (form.start_date && form.end_date) {
      const y1 = new Date(form.start_date).getFullYear();
      const y2 = new Date(form.end_date).getFullYear();
      setForm((f) => ({ ...f, name: `FY ${y1}/${String(y2).slice(-2)}` }));
    }
  };

  return (
    <AppContainer
      title="Financial Years"
      previousPageTitle="Accounting"
      description="Financial years define the accounting periods. A locked year prevents new postings. A closed year is fully archived. Only one year should be current/active at a time."
      actions={[{ label: "New Financial Year", icon: "fa-solid fa-plus", className: "btn btn-primary", onClick: () => setShowModal(true) }]}
    >
      <Toaster />
      <div className="card custom-card p-3">
        <DataTable columns={columns} data={years} loading={loading} pageSize={10} emptyMessage="No financial years configured." />
      </div>

      {showModal && (
        <AppModal
          title="Create Financial Year"
          onClose={() => setShowModal(false)}
          footer={
            <>
              <button className="btn btn-light" onClick={() => setShowModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleCreate} disabled={saving}>{saving ? "Creating..." : "Create"}</button>
            </>
          }
        >
          <div className="row g-3">
            <div className="col-lg-6">
              <label className="form-label tx-12 fw-semibold">Start Date</label>
              <input type="date" className="form-control" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} onBlur={suggestName} />
            </div>
            <div className="col-lg-6">
              <label className="form-label tx-12 fw-semibold">End Date</label>
              <input type="date" className="form-control" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} onBlur={suggestName} />
            </div>
            <div className="col-12">
              <label className="form-label tx-12 fw-semibold">Name <span className="tx-danger">*</span></label>
              <input type="text" className="form-control" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. FY 2025/26" />
              <small className="tx-muted">Set start and end dates to auto-suggest a name.</small>
            </div>
          </div>
        </AppModal>
      )}
    </AppContainer>
  );
};

export default FinancialYear;
